import { useContext } from 'react';

import { PermissionsContext } from './PermissionsProvider';
import type { PermissionsContextValue } from './types';

/**
 * Hook to access permissions from the PermissionsProvider.
 *
 * @throws Error if used outside of a PermissionsProvider
 *
 * @example
 * const { hasPermission, isAgent } = usePermissions();
 *
 * if (hasPermission('claim.update')) {
 *   // Show update button
 * }
 *
 * if (isAgent) {
 *   // Show agent-specific UI
 * }
 */
export const usePermissions = (): PermissionsContextValue => {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }

  return context;
};

/**
 * Safe version of usePermissions that returns null when outside provider.
 * Useful for components that may be rendered outside the permissions context.
 */
export const useSafePermissions = (): PermissionsContextValue | null => {
  return useContext(PermissionsContext);
};
